import UAParser from 'ua-parser-js';
import { env } from '../utils/env';
import type { UserModel } from './users';

const JOB = 'web-client';

type LOG_LEVELS = 'info' | 'error' | 'warn';

type InfoData = Record<string, unknown>;

type ErrorData = { message?: string; error?: unknown; stack?: string };

type LogArgs = ['error', unknown] | ['info', InfoData] | ['warn', InfoData];

const parseError = (error: unknown): ErrorData => {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack?.toString(),
    };
  }

  return { error };
};

export class LoggerService {
  #uaParser: UAParser;

  #user?: UserModel;

  constructor(private appName: string) {
    this.#uaParser = new UAParser();
  }

  setUser(user: UserModel) {
    this.#user = user;
  }

  log(...args: LogArgs) {
    this.#sendOrLog(...args);
  }

  error(error: unknown) {
    this.#sendOrLog('error', error);
  }

  info(info: InfoData) {
    this.#sendOrLog('info', info);
  }

  warn(info: InfoData) {
    this.#sendOrLog('warn', info);
  }

  #prepareBody(level: LOG_LEVELS, logInfo: InfoData | ErrorData) {
    const os = this.#uaParser.getOS();
    const browser = this.#uaParser.getBrowser();

    return {
      streams: [
        {
          stream: {
            job: JOB,
            app: this.appName,
            host: window.location.hostname,
            level,
            user: this.#user,
            url: window.location.href,
          },
          values: [[LoggerService.#now(), JSON.stringify({ os, browser, ...logInfo })]],
        },
      ],
    };
  }

  #sendOrLog(...args: LogArgs) {
    const [level, arg] = args;
    // TODO: use feature flags to activate logger
    if (!env.LOG_URL) {
      // eslint-disable-next-line no-console
      console[level]('LoggerService:', arg);
      return;
    }

    const getLogInfo = () => {
      if (level === 'error') {
        return parseError(arg);
      }

      return arg;
    };

    const body = this.#prepareBody(level, getLogInfo());
    LoggerService.#send(body);
  }

  static #send(body: unknown) {
    const headers = {
      type: 'application/json',
    };
    const blob = new Blob([JSON.stringify(body)], headers);
    navigator.sendBeacon(env.LOG_URL, blob);
  }

  static #now() {
    return `${Date.now() * 1_000_000}`;
  }
}
